'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { CalendarClock, Clock, MapPin, ShieldCheck, Users, X } from 'lucide-react'
import type { BookingProduct } from '@sifly/shared-types'
import {
  buildSessionSnapshot,
  formatCents,
  type SessionAddonDef,
  type SessionLadderEntry,
  type SessionSnapshot,
} from '@sifly/shared-utils'
import PhoneInput from '@sifly/shared-ui/content/LazyPhoneInput'
import { bookingTaxDisplay } from './_lib/bookingTaxDisplay'
import { SessionCalendar } from './SessionCalendar'
import { CallToBook } from './CallToBook'
import { LocationMap, type Pickup } from './LocationMap'
import { locationKindCopy } from '@/lib/locationKind'
import { monthStartOfYmd } from './_lib/bookingCalendarModel'
import {
  cutoffLabel,
  durationLabel,
  eligiblePartySizes,
  formatLocalDateLabel,
  formatSlotTime,
  type SessionMonthDay,
  type SessionSlot,
} from './_lib/sessionSlotModel'
import { startSessionCheckout, type SessionCheckoutResult } from './_lib/sessionCheckoutClient'
import { confirmBookingNow } from './_lib/confirmActions'
import { useBookingContact } from './_lib/useBookingContact'
import { formChrome } from './_lib/formLayout'
import { useStepSheetModal } from './_lib/useStepSheetModal'
import { BookingFlowRunner } from '../../_flow/BookingFlowRunner'
import { BookingLinkKeepsake } from './BookingLinkKeepsake'
import PartnerStrip from '@/components/checkout/PartnerStrip'
import ReferralQuestion from '@/components/checkout/ReferralQuestion'

// PaymentStep (Stripe Elements) is browser-only and reached only in the pay
// phase — code-split it so the initial PDP bundle skips the chunk until the
// visitor gets there.
const PaymentStep = dynamic(() => import('./PaymentStep').then(m => m.PaymentStep), {
  ssr: false,
  loading: () => (
    <div className="py-8 text-center text-sm text-[var(--color-text-secondary)]">Preparing secure payment…</div>
  ),
})

type Props = {
  product: BookingProduct & { bookable_resources: { id: string; name: string; capacity: number } | null }
  stripeAccountId: string | null
  /** The dealer's published waiver terms (prose markdown), shown in the signing
   *  surface's scroll-to-read box. Null when the dealer hasn't published a
   *  waiver page (the signature pad isn't rendered in that case). */
  waiverTerms?: string | null
  /** Dealer minors policy + floor (tenants.allow_minors / min_rider_age). */
  allowMinors?: boolean
  minRiderAge?: number
  /** Resolved product hero image (CF Images URL + alt), or null. */
  heroImage?: { url: string; alt: string | null } | null
  /** Resolved meeting-point location (name + locality + coords + directions), or null. */
  pickup?: Pickup | null
  /** Public tenant phone (null when the dealer hides it). Powers the
   *  call-to-book escape hatch on days the web checkout can't sell. */
  phone?: string | null
  /** 'full' = the /bookings page (default, unchanged). 'embed' = booking_widget
   *  content section: selection inline, checkout phases in the overlay. */
  layout?: 'full' | 'embed'
  /** Today in the pickup location's zone, 'YYYY-MM-DD', resolved on the
   *  server. See BookingForm — the form never reads its own clock. */
  today: string
  /** Sales tax percent resolved server-side (bookingProductContext), or null. */
  taxRatePct?: number | null
  /** True when the server couldn't resolve tax inputs — show "added at
   *  checkout" instead of claiming a tax-inclusive total. */
  taxUnknown?: boolean
}
type Phase = 'choose' | 'contact' | 'pay' | 'confirmed'

/**
 * What to say when the selected day hands back zero bookable times.
 *
 * Until the API started reporting WHY it filtered, all three causes shared one
 * line — "No times left that day" — and the most common cause was the one it
 * described worst. A session past its booking cutoff still runs and still has
 * seats; only the web checkout has shut. On AE's Lake Powell lesson the cutoff
 * is 7 days, so nearly every date a visitor can realistically pick lands here
 * and got told, wrongly, that the day was full.
 *
 * Ordering is deliberate: past-cutoff wins when a day is BOTH (one session full,
 * another past cutoff), because the one the dealer can still sell is the more
 * useful answer.
 */
function DayEmptyState({
  phone, dateLabel, withheld,
}: {
  phone: string | null | undefined
  dateLabel: string
  withheld: { pastCutoff: number; soldOut: number }
}) {
  if (withheld.pastCutoff > 0 && phone) {
    return (
      <CallToBook
        phone={phone}
        message={`Online booking has closed for ${dateLabel} — we may still have space.`}
      />
    )
  }
  if (withheld.soldOut > 0 && phone) {
    return (
      <CallToBook
        phone={phone}
        message={`${dateLabel} is fully booked. Call us to ask about cancellations, or try another date.`}
      />
    )
  }
  return (
    <p className="text-center text-sm text-[var(--color-text-muted)]">
      No times left that day — try another date.
    </p>
  )
}

export function SessionBookingForm({
  product, stripeAccountId, waiverTerms, allowMinors = false, minRiderAge = 12, heroImage, pickup, phone, layout = 'full', today,
  taxRatePct = null, taxUnknown = false,
}: Props) {
  const [month, setMonth] = useState(() => monthStartOfYmd(today))
  // Stable for the session — today's local date is the calendar's floor.
  const [minDate] = useState(today)
  const [monthDays, setMonthDays] = useState<Record<string, SessionMonthDay[]>>({})
  const [calLoading, setCalLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [daySlots, setDaySlots] = useState<SessionSlot[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  // Occurrences the API filtered out of `daySlots`, by reason. Only read when
  // the day comes back empty — it's what makes the dead end honest.
  const [withheld, setWithheld] = useState<{ pastCutoff: number; soldOut: number }>({ pastCutoff: 0, soldOut: 0 })
  const [selectedStartsAt, setSelectedStartsAt] = useState<string | null>(null)
  const [riders, setRiders] = useState<number | null>(null)
  const [addonKeys, setAddonKeys] = useState<string[]>([])
  const [phase, setPhase] = useState<Phase>('choose')
  // Contact fields persist to sessionStorage (tenant-scoped) so a card decline,
  // reload, or app-switch mid-checkout doesn't wipe them — restored on mount,
  // cleared on a confirmed booking. See useBookingContact / bookingContactStorage.
  const { contact, setContact, returnedAfterFailure, markPaymentFailed, clear: clearBookingContact, dismissReturnBanner } =
    useBookingContact(product.tenant_id)
  const [checkout, setCheckout] = useState<SessionCheckoutResult | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Raw durable flow token from confirmBookingNow — powers the keepsake link on
  // the confirmation screen. null until a confirmed mint returns one.
  const [flowToken, setFlowToken] = useState<string | null>(null)

  const ladder = useMemo<SessionLadderEntry[]>(
    () => (Array.isArray(product.party_pricing) ? (product.party_pricing as unknown as SessionLadderEntry[]) : []),
    [product.party_pricing],
  )
  const productAddons = useMemo<SessionAddonDef[]>(
    () => (Array.isArray(product.addons) ? (product.addons as unknown as SessionAddonDef[]) : []),
    [product.addons],
  )

  // Load the visible month (single-month calendar — one fetch per month; the
  // monthDays cache keeps paging back instant).
  const loadMonth = useCallback(async (m: string) => {
    setCalLoading(true)
    const res = await fetch(`/api/bookings/session-availability?productId=${product.id}&month=${m}`)
    const json = (await res.json().catch(() => ({}))) as { days?: SessionMonthDay[] }
    setMonthDays(prev => ({ ...prev, [m]: Array.isArray(json.days) ? json.days : [] }))
    setCalLoading(false)
  }, [product.id])

  useEffect(() => { loadMonth(month) }, [month, loadMonth])

  // Load the selected day's slots (bookable occurrences only — the API filters
  // cutoff-passed and full slots server-side).
  const loadDaySlots = useCallback(async (date: string) => {
    setSlotsLoading(true)
    const res = await fetch(`/api/bookings/session-availability?productId=${product.id}&date=${date}`)
    const json = (await res.json().catch(() => ({}))) as {
      slots?: SessionSlot[]
      withheld?: { pastCutoff?: number; soldOut?: number }
    }
    setDaySlots(Array.isArray(json.slots) ? json.slots : [])
    // Why the day came back empty, so the empty state can say something true.
    setWithheld({
      pastCutoff: Number(json.withheld?.pastCutoff) || 0,
      soldOut: Number(json.withheld?.soldOut) || 0,
    })
    setSlotsLoading(false)
  }, [product.id])

  useEffect(() => {
    setSelectedStartsAt(null)
    setRiders(null)
    if (!selectedDate) { setDaySlots([]); return }
    void loadDaySlots(selectedDate)
  }, [selectedDate, loadDaySlots])

  // A flat-rate product (booking_products.flat_rate_cents set) prices the same
  // at every party size and therefore ships an EMPTY party_pricing ladder by
  // design — the headcount selector below must fall back to rider_capacity
  // instead of reading (zero) ladder entries.
  const isFlatRate = product.flat_rate_cents != null
  const selectedSlot = daySlots.find(s => s.startsAt === selectedStartsAt) ?? null
  const eligible = selectedSlot
    ? eligiblePartySizes(ladder, selectedSlot.remaining, {
        flatRate: isFlatRate,
        riderCapacity: product.rider_capacity ?? 0,
      })
    : []
  const selectedAddons = productAddons.filter(a => addonKeys.includes(a.key))

  // Display-parity total from the same builder the server uses. The server
  // rebuilds and the hold RPC re-validates — this number is never sent.
  let snapshot: SessionSnapshot | null = null
  if (riders != null) {
    try {
      snapshot = buildSessionSnapshot({
        riders,
        ladder,
        addons: selectedAddons,
        flatRateCents: product.flat_rate_cents ?? undefined,
      })
    } catch {
      snapshot = null
    }
  }

  const taxDisplay = snapshot
    ? bookingTaxDisplay(snapshot.totalCents, taxRatePct, product.tax_treatment)
    : null

  const resetSelection = useCallback(async () => {
    setSelectedStartsAt(null)
    setRiders(null)
    await loadMonth(month)
    if (selectedDate) await loadDaySlots(selectedDate)
  }, [month, selectedDate, loadMonth, loadDaySlots])

  const reserve = async () => {
    if (!selectedSlot || riders == null) return
    setNotice(null)
    const r = await startSessionCheckout({
      productId: product.id,
      startsAt: selectedSlot.startsAt,
      riders,
      addonKeys,
      contact,
      releaseBookingId: checkout?.bookingId,
    })
    if (!r.ok) {
      if (r.status === 409) {
        setNotice('That time was just taken — pick another.')
        setPhase('choose')
        await resetSelection()
      } else {
        setNotice(r.error)
      }
      return
    }
    setCheckout(r.data)
    setPhase('pay')
  }

  const onPaid = async (piId: string) => {
    if (!checkout) return
    const res = await confirmBookingNow({
      bookingId: checkout.bookingId,
      paymentIntentId: piId,
      stripeAccountId: checkout.stripeAccountId,
    })
    if (res.flowToken) setFlowToken(res.flowToken)
    clearBookingContact()
    setPhase('confirmed')
  }

  const bookedRiders = checkout?.price.riders ?? riders ?? 1

  const onExpire = useCallback(() => {
    setCheckout(null)
    setPhase('choose')
    void resetSelection()
  }, [resetSelection])

  const policy = (product.cancellation_policy ?? {}) as { full_refund_hours?: number }
  const slotLabel = selectedSlot
    ? `${formatLocalDateLabel(selectedSlot.localDate)} · ${formatSlotTime(selectedSlot.startTime)}`
    : null
  const chrome = formChrome({ layout, phase, hasSelection: selectedDate != null })
  const rootRef = useRef<HTMLDivElement>(null)
  const overlayRef = useStepSheetModal({
    open: phase !== 'choose',
    allBreakpoints: chrome.overlayAllBreakpoints,
    onClose: onExpire,
    returnFocusRef: rootRef,
  })

  // Trust / details facts — built only from real product fields.
  type Detail = { icon: ComponentType<{ className?: string }>; label: string; value: string }
  const details: Detail[] = []
  // The resolved pickup, NOT the resource name: a resource is a fleet ("eFoil
  // Board Set: Page, AZ"), which is not a place, and since the meeting-point
  // override (migration 357) it need not even be at the same place. Reading the
  // resource here printed the fleet's internal name under a "Meeting point"
  // label while the pickup block further down the same page named the real
  // location. Prefer the customer-facing short_name, as that block does.
  if (pickup) {
    // Label follows the location KIND: a lesson meets lakeside, a rental is
    // collected from a shop. Hardcoding either word made this row wrong for
    // half of a dealer's products. See locationKind.ts.
    details.push({
      icon: MapPin,
      label: locationKindCopy(pickup.kind ?? 'unknown').detailLabel,
      value: pickup.shortName ?? pickup.name,
    })
  }
  if (product.duration_minutes != null && product.duration_minutes > 0) {
    details.push({ icon: Clock, label: 'Duration', value: durationLabel(product.duration_minutes) })
  }
  if (product.rider_capacity != null && product.rider_capacity > 0) {
    details.push({ icon: Users, label: 'Group size', value: `Up to ${product.rider_capacity} rider${product.rider_capacity === 1 ? '' : 's'}` })
  }
  if (policy.full_refund_hours != null) {
    details.push({ icon: ShieldCheck, label: 'Cancellation', value: `Free up to ${policy.full_refund_hours}h before your session` })
  }
  const cutoff = cutoffLabel(product.cutoff_minutes ?? 0)
  if (cutoff) {
    details.push({ icon: CalendarClock, label: 'Lead time', value: cutoff })
  }

  // ── Booking panel (right col on desktop; step-sheet body on mobile) ──
  const panel = (
    <div className="space-y-5">
      {/* Return-after-decline banner — calm warning token, explains why the form
          is prefilled after a failed payment. One-shot per return; auto-clears on
          a confirmed booking, dismissible meanwhile. */}
      {returnedAfterFailure && phase !== 'confirmed' && (
        <div className="flex items-start gap-3 rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-surface)] px-4 py-3 text-sm text-[var(--color-warning-strong)]">
          <p className="flex-1">Your payment didn&apos;t go through — your details are saved. Try another card.</p>
          <button
            type="button"
            onClick={dismissReturnBanner}
            aria-label="Dismiss"
            className="-mr-1 shrink-0 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Selected session — survives paging months on mobile. */}
      {slotLabel && (
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)] p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Your session</p>
          <p className="mt-0.5 text-sm font-medium text-[var(--color-text-primary)]">{slotLabel}</p>
        </div>
      )}

      {/* Price — always at top of panel. Flat-rate products price the same at
          every party size, so the base shows immediately rather than waiting
          on a headcount pick; per-rider add-ons still layer on top once
          they're selected. */}
      {isFlatRate ? (
        <div>
          <p className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {formatCents(product.flat_rate_cents!)}{' '}
            <span className="text-sm font-normal text-[var(--color-text-muted)]">
              · up to {product.rider_capacity} people
            </span>
          </p>
          {snapshot && (selectedAddons.length > 0 || taxDisplay?.taxLine || taxUnknown) && (
            <>
              {selectedAddons.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {selectedAddons.map(a => (
                    <li key={a.key} className="flex justify-between text-sm text-[var(--color-text-muted)]">
                      <span>{a.label ?? a.key}{a.per === 'rider' ? ` × ${snapshot!.riders}` : ''}</span>
                      <span>{formatCents(a.price_cents * (a.per === 'rider' ? snapshot!.riders : 1))}</span>
                    </li>
                  ))}
                </ul>
              )}
              {taxUnknown ? (
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                  Sales tax, if any, is added at checkout.
                </p>
              ) : (
                taxDisplay?.taxLine && (
                  <p className="mt-2 flex justify-between text-sm text-[var(--color-text-muted)]">
                    <span>{taxDisplay.taxLine.label}</span>
                    <span>{formatCents(taxDisplay.taxLine.amountCents)}</span>
                  </p>
                )
              )}
              <p className="mt-2 text-sm font-medium text-[var(--color-text-primary)]">
                Total: {formatCents(taxDisplay!.totalCents)}
              </p>
            </>
          )}
        </div>
      ) : snapshot ? (
        <div>
          <p className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {formatCents(taxDisplay!.totalCents)}{' '}
            <span className="text-sm font-normal text-[var(--color-text-muted)]">
              · {snapshot.riders} rider{snapshot.riders === 1 ? '' : 's'}
            </span>
          </p>
          {(selectedAddons.length > 0 || taxDisplay?.taxLine || taxUnknown) && (
            <ul className="mt-2 space-y-1">
              {selectedAddons.map(a => (
                <li key={a.key} className="flex justify-between text-sm text-[var(--color-text-muted)]">
                  <span>{a.label ?? a.key}{a.per === 'rider' ? ` × ${snapshot!.riders}` : ''}</span>
                  <span>{formatCents(a.price_cents * (a.per === 'rider' ? snapshot!.riders : 1))}</span>
                </li>
              ))}
              {taxUnknown ? (
                <li className="text-sm text-[var(--color-text-muted)]">
                  Sales tax, if any, is added at checkout.
                </li>
              ) : (
                taxDisplay?.taxLine && (
                  <li className="flex justify-between text-sm text-[var(--color-text-muted)]">
                    <span>{taxDisplay.taxLine.label}</span>
                    <span>{formatCents(taxDisplay.taxLine.amountCents)}</span>
                  </li>
                )
              )}
            </ul>
          )}
        </div>
      ) : (
        <p className="text-[var(--color-text-muted)]">Pick a date and time to see the price</p>
      )}

      {/* choose: calendar → slot list → party size → addons → Reserve */}
      {phase === 'choose' && (
        <>
          <SessionCalendar
            monthStart={month}
            daysByMonth={monthDays}
            selected={selectedDate}
            minDate={minDate}
            loading={calLoading}
            onDateChange={setSelectedDate}
            onMonthChange={setMonth}
            hint="Select a date to see available times"
            phone={phone}
          />

          {selectedDate && (
            slotsLoading ? (
              <div className="grid grid-cols-3 gap-2" aria-hidden="true">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-11 rounded bg-[var(--color-border)]/40 animate-pulse" />
                ))}
              </div>
            ) : daySlots.length === 0 ? (
              // Three different dead ends wearing one message until now. Past
              // cutoff = the session runs and has room, the web store just
              // closed; that is the one worth a phone call, and on a 7-day
              // cutoff it is most of the calendar. Sold out = only cancellations
              // are left, so the copy promises nothing. Neither = the day has no
              // occurrences at all, which the calendar should already have
              // disabled; keep the original line as the floor.
              <DayEmptyState
                phone={phone}
                dateLabel={formatLocalDateLabel(selectedDate)}
                withheld={withheld}
              />
            ) : (
              <div>
                <p className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">
                  Times for {formatLocalDateLabel(selectedDate)}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {daySlots.map(s => {
                    const isSel = s.startsAt === selectedStartsAt
                    return (
                      <button
                        key={s.startsAt}
                        type="button"
                        onClick={() => {
                          setSelectedStartsAt(isSel ? null : s.startsAt)
                          setRiders(null)
                        }}
                        aria-pressed={isSel}
                        className={[
                          'min-h-[44px] rounded-md border text-sm transition-colors',
                          isSel
                            ? 'border-[var(--color-selected)] bg-[var(--color-selected)] text-[var(--color-selected-text)]'
                            : 'border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-border)]/40',
                        ].join(' ')}
                      >
                        <span className="block font-medium">{formatSlotTime(s.startTime)}</span>
                        <span className={`block text-xs ${isSel ? 'text-[var(--color-selected-text)]' : 'text-[var(--color-text-muted)]'}`}>
                          {s.remaining} spot{s.remaining === 1 ? '' : 's'} left
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          )}

          {selectedSlot && eligible.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">Riders</p>
              <div className="flex flex-wrap gap-2">
                {eligible.map(n => {
                  const isSel = riders === n
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRiders(isSel ? null : n)}
                      aria-pressed={isSel}
                      className={[
                        'min-h-[44px] min-w-[52px] rounded-md border px-3 text-sm transition-colors',
                        isSel
                          ? 'border-[var(--color-selected)] bg-[var(--color-selected)] text-[var(--color-selected-text)]'
                          : 'border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-border)]/40',
                      ].join(' ')}
                    >
                      {n}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {selectedSlot && eligible.length === 0 && (
            <p className="text-sm text-[var(--color-text-muted)]">
              Not enough spots left for the smallest group — try another time.
            </p>
          )}

          {selectedSlot && riders != null && productAddons.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">Add-ons</p>
              <div className="space-y-1">
                {productAddons.map(a => {
                  const checked = addonKeys.includes(a.key)
                  // String-guarded: addons is a raw JSON column — a malformed
                  // note/description must not crash the booking widget.
                  const note = typeof a.note === 'string' && a.note.trim() !== '' ? a.note : null
                  const description =
                    typeof a.description === 'string' && a.description.trim() !== ''
                      ? a.description
                      : null
                  return (
                    <div key={a.key}>
                      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e =>
                            setAddonKeys(prev => (e.target.checked ? [...prev, a.key] : prev.filter(k => k !== a.key)))
                          }
                          className="size-5 shrink-0 accent-[var(--color-selected)]"
                        />
                        <span className="flex-1 text-sm text-[var(--color-text-primary)]">
                          {a.label ?? a.key}
                          {note && (
                            <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">{note}</span>
                          )}
                        </span>
                        <span className="text-sm text-[var(--color-text-muted)]">
                          {formatCents(a.price_cents)}{a.per === 'rider' ? ' / rider' : ''}
                        </span>
                      </label>
                      {description && (
                        // Plain text only (React-escaped); pre-line preserves the
                        // dealer's paragraphs. Scrolls internally so long legal
                        // copy never pushes Reserve off-screen.
                        <div className="mb-1 ml-8 max-h-56 overflow-y-auto whitespace-pre-line rounded-md border border-[var(--color-border)] p-3 text-xs leading-relaxed text-[var(--color-text-secondary)]">
                          {description}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {notice && <p className="text-sm text-[var(--color-text-secondary)]">{notice}</p>}
          <button
            type="button"
            disabled={!snapshot}
            onClick={() => setPhase('contact')}
            className="w-full rounded-md bg-[var(--color-cta)] py-3 text-[var(--color-cta-text)] font-medium disabled:opacity-60"
          >
            Reserve
          </button>
        </>
      )}

      {/* contact: email + name + phone → startSessionCheckout */}
      {phase === 'contact' && (
        <form onSubmit={e => { e.preventDefault(); void reserve() }} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="sbk-first" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">First name</label>
              <input
                id="sbk-first" required autoComplete="given-name" placeholder="Duke"
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
                value={contact.firstName}
                onChange={e => setContact({ ...contact, firstName: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="sbk-last" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Last name</label>
              <input
                id="sbk-last" required autoComplete="family-name" placeholder="Kahanamoku"
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
                value={contact.lastName}
                onChange={e => setContact({ ...contact, lastName: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label htmlFor="sbk-email" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Email</label>
            <input
              id="sbk-email" required type="email" autoComplete="email" placeholder="you@example.com"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]"
              value={contact.email}
              onChange={e => setContact({ ...contact, email: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="sbk-phone" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Phone</label>
            <PhoneInput
              id="sbk-phone"
              value={contact.phone}
              onChange={e164 => setContact(c => ({ ...c, phone: e164 }))}
              defaultCountry="US"
              favoriteCountries={['US', 'CA']}
              required
              placeholder="(555) 123-4567"
            />
          </div>
          {notice && <p className="text-sm text-[var(--color-text-secondary)]">{notice}</p>}
          <button type="submit" className="w-full rounded-md bg-[var(--color-cta)] py-3 text-[var(--color-cta-text)] font-medium">
            Continue to payment
          </button>
        </form>
      )}

      {/* pay: Payment Element + countdown */}
      {phase === 'pay' && checkout && (
        <>
          <div className="mb-3 empty:hidden"><PartnerStrip /></div>
          <PaymentStep checkout={checkout} onSucceeded={onPaid} onExpire={onExpire} onPaymentError={markPaymentFailed} />
        </>
      )}

      {/* confirmed: booking summary + the pinned post-checkout flow */}
      {phase === 'confirmed' && checkout && (
        <div className="space-y-4">
          <div className="rounded-md border border-[var(--color-border)] p-4">
            <p className="font-medium text-[var(--color-text-primary)]">You&apos;re booked!</p>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              {slotLabel} · {bookedRiders} rider{bookedRiders === 1 ? '' : 's'}
            </p>
          </div>
          <BookingFlowRunner
            flow={checkout.flow}
            capability={{
              kind: 'pi',
              bookingId: checkout.bookingId,
              paymentIntentId: checkout.paymentIntentId,
              stripeAccountId: checkout.stripeAccountId,
            }}
            booking={{ mode: 'session', requiredRiders: bookedRiders, signedSlots: [] }}
            waiverTerms={waiverTerms ?? null}
            policy={{ minRiderAge, allowMinors }}
          />
          {flowToken && <BookingLinkKeepsake token={flowToken} />}
          <div className="empty:hidden"><ReferralQuestion kind="booking" saleId={checkout.bookingId} /></div>
        </div>
      )}
    </div>
  )

  const priceLabel = snapshot
    ? `${formatCents(taxDisplay!.totalCents)} · ${snapshot.riders} rider${snapshot.riders === 1 ? '' : 's'}`
    : isFlatRate
      ? `${formatCents(product.flat_rate_cents!)} · up to ${product.rider_capacity} people`
      : 'Pick a time'

  // Left column: hero image, title/description, and a details list.
  const overview = (
    <div>
      {heroImage && (
        <div className="relative mb-6 w-full aspect-[4/3] overflow-hidden rounded-lg">
          <Image
            src={heroImage.url}
            alt={heroImage.alt ?? product.title}
            fill
            priority
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="object-cover"
          />
        </div>
      )}
      <h1 className="text-3xl font-semibold text-[var(--color-text-primary)]">{product.title}</h1>
      {product.description && (
        <p className="mt-3 text-[var(--color-text-secondary)]">{product.description}</p>
      )}
      {details.length > 0 && (
        <ul className="mt-6 space-y-4 border-t border-[var(--color-border)] pt-6">
          {details.map(d => (
            <li key={d.label} className="flex items-start gap-3 text-sm">
              <d.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-cta-readable)]" aria-hidden="true" />
              <div>
                <p className="font-medium text-[var(--color-text-primary)]">{d.value}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{d.label}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  // ── No Stripe connected account — calm pre-flight guard ──────────────────────
  if (!stripeAccountId) {
    return (
      <div className={chrome.pageChrome ? 'max-w-6xl mx-auto px-4 py-12' : undefined}>
        <div className={chrome.showOverview ? 'grid grid-cols-1 lg:grid-cols-2 gap-12' : undefined}>
          {chrome.showOverview && overview}
          <div className="rounded-md border border-[var(--color-border)] p-6 space-y-2">
            <p className="font-medium text-[var(--color-text-primary)]">
              Online booking isn&apos;t available for this location yet.
            </p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Contact the dealer directly to reserve.
            </p>
          </div>
        </div>
        {chrome.showMap && pickup && <LocationMap pickup={pickup} />}
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      // Focus target when the step sheet closes (see useStepSheetModal). Not a
      // Tab stop, so the ring it would paint around the whole form is noise.
      tabIndex={-1}
      className={
        chrome.pageChrome
          ? 'focus:outline-none max-w-6xl mx-auto px-4 pt-12 pb-28 lg:pb-12'
          : chrome.showStickyBar
            ? 'focus:outline-none pb-28 lg:pb-0' // room for the fixed sticky bar on mobile
            : 'focus:outline-none'
      }
    >
      <div className={chrome.showOverview ? 'grid grid-cols-1 lg:grid-cols-2 gap-12' : undefined}>
        {/* Left: hero + title + description + details */}
        {chrome.showOverview && overview}

        {/* Booking panel in the page flow. 'full': hidden on mobile once the
            step sheet takes over (historical behavior). 'embed': only mounted
            during the choose phase — checkout lives in the overlay. */}
        {chrome.showDesktopInlinePanel && (
          <div className={phase !== 'choose' && chrome.pageChrome ? 'hidden lg:block' : undefined}>{panel}</div>
        )}
      </div>

      {/* Meeting-point map — full-width, under the fold */}
      {chrome.showMap && pickup && <LocationMap pickup={pickup} />}

      {/* ── Mobile sticky CTA bar (choose phase; embed: only once a date is picked) ── */}
      {chrome.showStickyBar && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 border-t border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                {slotLabel ?? 'Pick your session'}
              </p>
              <p className="truncate text-xs text-[var(--color-text-muted)]">
                {snapshot || isFlatRate ? priceLabel : selectedDate ? 'Choose a time' : 'Date & time on the calendar'}
              </p>
            </div>
            <button
              type="button"
              disabled={!snapshot}
              onClick={() => setPhase('contact')}
              className="flex-shrink-0 rounded-md bg-[var(--color-cta)] px-5 py-2.5 text-sm font-medium text-[var(--color-cta-text)] disabled:opacity-60"
            >
              Reserve
            </button>
          </div>
        </div>
      )}

      {/* ── Step-sheet overlay (contact / pay / confirmed). 'full': mobile only.
          'embed': all breakpoints — the focused checkout surface. ── */}
      {phase !== 'choose' && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="Complete your booking"
          className={`${chrome.overlayAllBreakpoints ? '' : 'lg:hidden '}fixed inset-0 z-50 bg-[var(--color-bg,#fff)] overflow-y-auto px-4 py-6`}
        >
          {(() => {
            const body = (
              <>
                <button
                  type="button"
                  aria-label="Close and release hold"
                  onClick={onExpire}
                  className="mb-6 flex items-center gap-1 text-sm text-[var(--color-text-secondary)]"
                >
                  <span aria-hidden="true">‹</span> Back
                </button>
                {panel}
              </>
            )
            return chrome.overlayAllBreakpoints ? <div className="mx-auto w-full max-w-lg">{body}</div> : body
          })()}
        </div>
      )}
    </div>
  )
}
